import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import ScrollProgressBar from '../../components/interactive/ScrollProgressBar';
import SampleScoreSlider from '../../components/interactive/SampleScoreSlider';

export default function ScoreExplainedPage() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const scoreRanges = [
    { min: 900, max: 1000, label: 'Excellent', color: 'bg-emerald-500', description: 'Outstanding health profile with optimal metrics across all categories' },
    { min: 800, max: 899, label: 'Very Good', color: 'bg-teal-500', description: 'Strong health indicators with minor areas for improvement' },
    { min: 700, max: 799, label: 'Good', color: 'bg-blue-500', description: 'Solid health foundation with some opportunities to optimize' },
    { min: 600, max: 699, label: 'Fair', color: 'bg-amber-500', description: 'Moderate health status with several areas needing attention' },
    { min: 0, max: 599, label: 'Needs Attention', color: 'bg-red-500', description: 'Multiple areas with high potential for improvement - consider consulting healthcare providers' }
  ];

  const categoryWeights = [
    { 
      name: 'Cardiovascular Health', 
      weight: 25, 
      icon: 'ri-heart-pulse-line',
      color: 'text-red-500',
      factors: [
        'Smoking status (major impact)',
        'Physical activity level',
        'Blood pressure readings',
        'Cardio exercise frequency',
        'Resting heart rate'
      ],
      improvements: [
        '150+ minutes moderate cardio weekly: +15-20 points',
        'Quit smoking: +20-25 points',
        'Maintain optimal BP (&lt;120/80): +10 points'
      ]
    },
    { 
      name: 'Metabolic Health', 
      weight: 25, 
      icon: 'ri-pulse-line',
      color: 'text-purple-500',
      factors: [
        'Body Mass Index (BMI)',
        'Blood sugar levels',
        'Nutrition quality',
        'Processed food intake',
        'Fruit & vegetable consumption'
      ],
      improvements: [
        'Achieve healthy BMI (18.5-24.9): +15 points',
        '5+ servings fruits/veggies daily: +10 points',
        'Reduce processed foods: +8 points'
      ]
    },
    { 
      name: 'Lifestyle Habits', 
      weight: 25, 
      icon: 'ri-run-line',
      color: 'text-teal-500',
      factors: [
        'Alcohol consumption',
        'Daily step count',
        'Strength training frequency',
        'Sedentary time',
        'Hydration habits'
      ],
      improvements: [
        '10,000+ daily steps: +10 points',
        'Strength training 2+ days/week: +10 points',
        'Moderate/no alcohol: +5-10 points'
      ]
    },
    { 
      name: 'Recovery & Sleep', 
      weight: 15, 
      icon: 'ri-moon-line',
      color: 'text-indigo-500',
      factors: [
        'Sleep duration (7-9 hours)',
        'Sleep quality',
        'Sleep consistency',
        'Recovery time',
        'Rest days'
      ],
      improvements: [
        '7-9 hours quality sleep: +15 points',
        'Consistent sleep schedule: +10 points',
        'Good sleep quality: +10 points'
      ]
    },
    { 
      name: 'Mental Wellbeing', 
      weight: 10, 
      icon: 'ri-mental-health-line',
      color: 'text-pink-500',
      factors: [
        'Stress level',
        'Stress management practices',
        'Work-life balance',
        'Social connections',
        'Mindfulness activities'
      ],
      improvements: [
        'Active stress management: +10 points',
        'Good work-life balance: +8 points',
        'Low stress levels: +15 points'
      ]
    }
  ];

  const exampleScores = [
    {
      score: 742,
      profile: 'Sarah, 34',
      breakdown: [
        { category: 'Cardiovascular', score: 78, color: 'bg-red-500' },
        { category: 'Metabolic', score: 72, color: 'bg-purple-500' },
        { category: 'Lifestyle', score: 68, color: 'bg-teal-500' },
        { category: 'Recovery', score: 80, color: 'bg-indigo-500' },
        { category: 'Mental', score: 75, color: 'bg-pink-500' }
      ],
      topFactors: [
        { text: 'Non-smoker', impact: '+15' },
        { text: 'Good sleep (8hrs)', impact: '+15' },
        { text: 'BMI 27 (overweight)', impact: '-10' }
      ]
    },
    {
      score: 856,
      profile: 'Michael, 28',
      breakdown: [
        { category: 'Cardiovascular', score: 92, color: 'bg-red-500' },
        { category: 'Metabolic', score: 88, color: 'bg-purple-500' },
        { category: 'Lifestyle', score: 85, color: 'bg-teal-500' },
        { category: 'Recovery', score: 78, color: 'bg-indigo-500' },
        { category: 'Mental', score: 82, color: 'bg-pink-500' }
      ],
      topFactors: [
        { text: 'Very active lifestyle', impact: '+20' },
        { text: 'Healthy BMI', impact: '+15' },
        { text: 'Regular strength training', impact: '+10' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      <ScrollProgressBar color="#14b8a6" height={4} showPercentage={true} />
      <TopNavigation />

      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 pt-24">
        <div className="container mx-auto px-4 py-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors mb-4 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Understanding Your Health Score</h1>
          <p className="text-lg text-gray-600">A transparent, explainable system similar to credit scoring</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Score Ranges */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Score Ranges (0-1000 Scale)</h2>
          <div className="space-y-4">
            {scoreRanges.map((range) => (
              <div key={range.label} className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-teal-300 transition-colors">
                <div className="flex items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className={`w-24 h-24 ${range.color} rounded-xl flex items-center justify-center text-white`}>
                      <div className="text-center">
                        <div className="text-2xl font-bold">{range.min}</div>
                        <div className="text-xs">to</div>
                        <div className="text-2xl font-bold">{range.max}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{range.label}</h3>
                    <p className="text-gray-600">{range.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Interactive Score Calculator */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Try It Yourself!</h2>
            <p className="text-lg text-gray-600">
              Experiment with the sliders below to see how different health factors affect your overall score
            </p>
          </div>
          <SampleScoreSlider />
        </section>

        {/* Category Weights */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">How Your Score is Calculated</h2>
          <p className="text-lg text-gray-600 mb-8">Your Health Score is a weighted combination of five key categories:</p>
          
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-8 mb-8">
            <div className="space-y-6">
              {categoryWeights.map((category) => (
                <div key={category.name}>
                  <div 
                    className="flex items-center justify-between mb-3 cursor-pointer"
                    onClick={() => setActiveCategory(activeCategory === category.name ? null : category.name)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg ${category.color}`}>
                        <i className={`${category.icon} text-2xl`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-600">{category.weight}% of total score</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-3xl font-bold text-gray-900">{category.weight}%</div>
                      <i className={`ri-arrow-${activeCategory === category.name ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div
                      className="bg-teal-500 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${category.weight}%` }}
                    ></div>
                  </div>

                  {activeCategory === category.name && (
                    <div className="bg-gray-50 rounded-xl p-6 mt-4 space-y-6 animate-slideDown">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">Key Factors:</h4>
                        <ul className="space-y-2">
                          {category.factors.map((factor, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                              <span dangerouslySetInnerHTML={{ __html: factor }}></span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-3">How to Improve:</h4>
                        <ul className="space-y-2">
                          {category.improvements.map((improvement, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <i className="ri-arrow-up-circle-fill text-green-600 mt-1"></i>
                              <span dangerouslySetInnerHTML={{ __html: improvement }}></span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-calculator-line text-xl text-teal-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Calculation Formula</h4>
                <p className="text-sm text-gray-700 mb-3">
                  Your overall Health Score is calculated as:
                </p>
                <div className="bg-white rounded-lg p-4 font-mono text-sm text-gray-800">
                  Score = (Cardiovascular × 0.25) + (Metabolic × 0.25) + (Lifestyle × 0.25) + (Recovery × 0.15) + (Mental × 0.10)
                </div>
                <p className="text-sm text-gray-600 mt-3">
                  Each category is scored 0-100, then weighted and combined to create your 0-1000 total score.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Example Scores */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Real Score Examples</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {exampleScores.map((example) => (
              <div key={example.profile} className="bg-white rounded-2xl border-2 border-gray-200 p-8">
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-gray-900 mb-2">{example.score}</div>
                  <div className="text-gray-600">{example.profile}</div>
                </div>

                <div className="space-y-3 mb-6">
                  {example.breakdown.map((cat) => (
                    <div key={cat.category}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">{cat.category}</span>
                        <span className="text-sm font-bold text-gray-900">{cat.score}/100</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${cat.color} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${cat.score}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t-2 border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Top Score Factors:</h4>
                  <div className="space-y-2">
                    {example.topFactors.map((factor, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">{factor.text}</span>
                        <span className={`font-bold ${factor.impact.startsWith('+') ? 'text-green-600' : 'text-red-600'}`}>
                          {factor.impact}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Future Integrations */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Future Enhancements</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-smartphone-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Wearable Integration</h3>
              <p className="text-sm text-gray-600 mb-3">Connect Apple Health, Google Fit, Fitbit for automatic data sync</p>
              <span className="inline-block px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap">Coming Soon</span>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mb-4">
                <i className="ri-test-tube-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Lab Value Tracking</h3>
              <p className="text-sm text-gray-600 mb-3">Import blood work, cholesterol, glucose readings for deeper insights</p>
              <span className="inline-block px-3 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-full whitespace-nowrap">Coming Soon</span>
            </div>

            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg mb-4">
                <i className="ri-brain-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">AI-Powered Insights</h3>
              <p className="text-sm text-gray-600 mb-3">Machine learning models for personalized predictions and recommendations</p>
              <span className="inline-block px-3 py-1 bg-teal-50 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap">In Development</span>
            </div>
          </div>
        </section>

        {/* Privacy & Ethics */}
        <section>
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Privacy & Ethics Commitment</h2>
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
                  <i className="ri-shield-check-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">What We Do</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Encrypt all health data end-to-end</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Give you full control over your data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Provide transparent scoring explanations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-green-600 mt-1"></i>
                    <span>Focus on improvement, not judgment</span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg mb-4">
                  <i className="ri-shield-cross-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">What We Never Do</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    <span>Sell your health data to third parties</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    <span>Share scores with insurers or employers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    <span>Use scores for discrimination</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-close-line text-red-600 mt-1"></i>
                    <span>Provide medical diagnosis or treatment</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t-2 border-gray-200">
              <p className="text-sm text-gray-600 leading-relaxed">
                <strong className="text-gray-900">Important:</strong> Your Health Score is for personal informational and motivational purposes only. It is not a medical diagnosis, does not replace professional medical advice, and cannot be used for insurance underwriting, employment decisions, or any form of discrimination. Always consult qualified healthcare providers for medical decisions.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
